import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  GenericKeyResponseDto,
  SearchKeysResponseDtoSigningKeyEntity,
} from '../../api/kmsapiclient'
import {
  removeSigningKeyAsync,
  searchSigningKeyAsync,
  uploadSigningKeysAsync,
} from '../../api/signingKeyApi'
import {
  DELETE_KEYS_PERMISSION,
  GET_KEYS_PERMISSION,
  UPLOAD_KEYS_PERMISSION,
} from '../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import {
  addSigningKeySuccess,
  deleteSigningKeySuccess,
  loadMoreSigningKeysSuccess,
  loadSigningKeysSuccess,
} from '../../redux/actions/signingKeysActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import { KeysReducerState } from '../../redux/reducers/initialState'
import { GetPageNum } from '../../utils'
import KeyList from '../common/KeyList'
import { usePermissions } from '../common/permission/usePermissions'
import { KeyInfo, SearchKeyInfo } from '../common/types'
import { validateKey } from '../common/utils'

export default function SigningKeysPage() {
  const keys: KeysReducerState = useSelector(
    (state: IRootState) => state.signingKeys,
  )
  const loading: boolean = useSelector(
    (state: IRootState) => state.apiCallsInProgress > 0,
  )
  const defaultSearchKeyRequest: SearchKeyInfo = {
    keyId: '',
    keyName: '',
    aadRevocation: '',
  }
  const [searchSigningKeysRequest, setSearchSigningKeysRequest] =
    useState<SearchKeyInfo>(defaultSearchKeyRequest)
  const disableLoadMore = !(
    searchSigningKeysRequest.continuationToken === '' ||
    searchSigningKeysRequest.continuationToken === null
  )
  const [pageNum, setPageNum] = useState(1)
  const [keysExists, setKeysExists] = useState(true)
  const firstUpdate = useRef(true)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { checkPermissions } = usePermissions()

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    setKeysExists(keys.keys.length > 0)

    if (validateKey(keys, searchSigningKeysRequest)) {
      setSearchSigningKeysRequest({
        ...searchSigningKeysRequest,
        continuationToken: keys.continuationToken,
      })
    }

    setSearchSigningKeysRequest(searchSigningKeysRequest)
  }, [keys.keys])

  const handleDeleteKey = async (signingKey: KeyInfo) => {
    checkPermissions(store.getState(), DELETE_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const result: string = await removeSigningKeyAsync(signingKey)
      if (result === 'Success') {
        dispatch(deleteSigningKeySuccess(signingKey))
        toast.success(t('key.key_deleted'))
      } else {
        dispatch(endApiCall())
        toast.error(t('key.key_delete_failed'))
      }
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const handleSearch = () => {
    searchKeys(searchSigningKeysRequest, loadSigningKeysSuccess)
  }

  const handleLoadMore = () => {
    searchKeys(searchSigningKeysRequest, loadMoreSigningKeysSuccess)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value)
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value.trim())
  }

  const setSearchCriteria = (name, value) => {
    const newGetKeysRequest = {
      ...searchSigningKeysRequest,
      [name]: value,
    }
    setSearchSigningKeysRequest(newGetKeysRequest)
  }

  const searchKeys = async (request, successFunc) => {
    checkPermissions(store.getState(), GET_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const keys: SearchKeysResponseDtoSigningKeyEntity =
        await searchSigningKeyAsync(request)

      dispatch(successFunc(keys))

      const length = [keys].length

      setPageNum(GetPageNum(length))
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const addSigningKey = async (file, keyName, aadRevocation) => {
    checkPermissions(store.getState(), UPLOAD_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const result: GenericKeyResponseDto = await uploadSigningKeysAsync(
        file,
        keyName,
        aadRevocation,
      )
      if (result != null) {
        dispatch(addSigningKeySuccess(result))
        toast.success(t('key.signing_keys_added'))
        setPageNum(1)
      } else {
        dispatch(endApiCall())
        toast.error(t('key.signing_keys_add_failed'))
      }
    } catch (error) {
      toast.error(t('key.signing_keys_add_failed'))
      dispatch(apiCallError())
      throw error
    }
  }

  return (
    <div className="container">
      <h3 className="page-header">{t('manage.signing_keys')}</h3>
      <KeyList
        keys={keys.keys}
        loading={loading}
        onSearchClick={handleSearch}
        onDeleteClick={handleDeleteKey}
        onLoadMoreClick={handleLoadMore}
        handleChange={handleChange}
        handleBlur={handleBlur}
        keysExists={keysExists}
        request={searchSigningKeysRequest}
        pageNum={pageNum}
        disableLoadMore={disableLoadMore}
        updatePageNum={setPageNum}
        editKeyPath="/signingkeys/"
        showUploadButton={true}
        uploadKey={addSigningKey}
      />
    </div>
  )
}
